import {
  Children,
  cloneElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { JDSDatepickerDesktopProps } from './generated';
import { debounce } from '@jds/utils';
import { setPositions } from './position';
import { useToggle } from '../../hooks/useToggle';

const DEBOUNCE_TIME = 400;

export interface DatePickerProps
  extends Omit<
    JDSDatepickerDesktopProps,
    | 'state'
    | 'monthName'
    | 'year'
    | 'calendarData'
    | 'offsetX'
    | 'offsetY'
    | 'onClose'
    | 'current'
    | 'months'
    | 'yearData'
    | 'onMonthSelect'
    | 'onYearSelect'
    | 'onPrev'
    | 'onNext'
    | 'onSelect'
  > {
  triggerRef?: React.MutableRefObject<HTMLElement | null>;
  onClose?: () => void;
  value?: string | Date;
  min?: string | Date;
  max?: string | Date;
  onMonthSelect?: () => void;
  onYearSelect?: () => void;
  /**
   * @deprecated use `onSelect` instead
   */
  onChange?: (value: Date) => void;
  onSelect?: (value: Date) => void;
  dateFilter?: (date: string | Date) => boolean;
  disabledDates?: Array<string | Date>;
  onConfirm?: (value: Date) => void;
  position?:
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'right-start'
    | 'right-end'
    | 'top'
    | 'left-start'
    | 'left-end'
    | 'bottom';
  children?: JSX.Element;
  /**
   * @deprecated use open prop
   */
  close?: boolean;
}

export const withLogic =
  (
    Component: React.ComponentType<DatePickerProps>
  ): React.FC<DatePickerProps> =>
  (props: DatePickerProps) => {
    const { onClose, close: userClose = true, open, ...rest } = props;
    const closeProp = typeof open === 'undefined' ? userClose : !open;

    const [containerElement, setContainerElement] = useState<HTMLDivElement>();
    const anchorRef = useRef<HTMLElement>(null);

    const trigger = props.triggerRef || anchorRef;

    const { selected, toggle, close } = useToggle(
      !closeProp,
      true,
      props.onClose
    );
    const [coords, setCoords] = useState({
      left: 0,
      top: 0
    });

    const updatePositions = useCallback(() => {
      if (selected) {
        const { left, top } = setPositions(
          props.position,
          containerElement,
          trigger?.current
        );

        if (containerElement) {
          window.setTimeout(function () {
            containerElement.classList.add('visible');
          }, 10);
        }

        setCoords({ left, top: top || 0 });
      }
    }, [trigger?.current, containerElement, props.position, selected]);

    useEffect(() => {
      updatePositions();
    }, [updatePositions]);

    const containerRef = useCallback((ref: HTMLDivElement) => {
      setContainerElement(ref);
    }, []);

    useEffect(() => {
      window?.addEventListener(
        'resize',
        debounce(updatePositions, DEBOUNCE_TIME)
      );
      return () =>
        window?.removeEventListener(
          'resize',
          debounce(updatePositions, DEBOUNCE_TIME)
        );
    }, [updatePositions]);

    const scrollCloseListener = useCallback(() => {
      close();
      onClose?.();
    }, [close, onClose]);

    useEffect(() => {
      window.addEventListener('scroll', scrollCloseListener);

      return () => {
        window.removeEventListener('scroll', scrollCloseListener);
      };
    }, [scrollCloseListener]);
    const [firstChild = <span></span>, ...restChildren] = Children.toArray(
      props.children
    );

    return (
      <>
        <Component
          {...{
            ...rest,
            open: selected,
            triggerRef: containerRef,
            onClose: () => {
              if (selected) {
                toggle();
              }
              onClose?.();
            },
            calendarData: {
              disabledDates: props.disabledDates,
              dateFilter: props.dateFilter,
              min: props.min,
              max: props.max
            },
            onClick: (ev: React.MouseEvent<HTMLDivElement>) =>
              ev.stopPropagation(),
            offsetX: coords.left,
            offsetY: coords.top,
            _onMonthSelect: (i: number) => {
              props.onMonthSelect?.();
            },
            _onYearSelect: (i: number) => {
              props.onYearSelect?.();
            }
          }}
        />
        {cloneElement(firstChild as React.ReactElement, { ref: anchorRef })}
        {restChildren}
      </>
    );
  };
